import { Group, Menu, Text } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { insertDualDialogue } from '#choo-app/lib/editor/plugins/editor-keymap/commands'
import { ElementKeyConfig } from '#choo-app/lib/editor/plugins/element-picker-keymap'
import { CustomIcon } from '#components/CustomIcon'
import { ElementIcon } from '#components/ElementIcon'
import { FancyMenuItem } from '#components/FancyMenuItem'
import { showCustomizeStylesModal } from '#components/Modals/CustomizeStyles'
import { Toolbar } from '#components/Toolbar'
import { Keys, useShortcuts } from '#hooks'
import { useMst } from '#state'
import { ButtonMode, ILoadedScript, IPopulatedPmEditor } from '#state/types'
import { insertPageBreak } from '#util'

import { InlineElementMenuButton } from './InlineElementMenuButton'

export const ElementMenu = observer(function ElementMenu({
  script,
  observableEditor,
  mode,
}: {
  script: ILoadedScript
  observableEditor: IPopulatedPmEditor
  mode: ButtonMode
}) {
  'use memo'

  const mst = useMst()

  const {
    elementPickerKeys,
    focusEditor,
    selectionBlockType,
    selectionTouchesEndOfBlock,
    singleBlockTextSelection,
  } = observableEditor

  const maxHeight = mst.view.dimensions.scriptScroller.height - 20

  const opened = script.elementMenuOpened
  const restricted = !!script.readOnlyByPlan

  const toggleMenu = () => {
    if (restricted && !opened) return
    script.setElementMenuOpened(!opened)
    // if the user just closed the menu, refocus
    if (opened) focusEditor()
  }

  const curr = selectionBlockType

  // if a user copy/pastes a block type into a document that doesn't belong
  // we won't find it in the call below
  // this means we won't display an icon in the menu target, but this doesnt
  // they can't interact with the menu
  const meta = elementPickerKeys.find((item) => item.node.name === curr)
  const selectionSpansBlocks = !singleBlockTextSelection
  const targetTitle = meta?.displayName ?? 'Multiple'

  const tooltip = 'Change block type'

  const { getItemProps } = useShortcuts({
    menu: {
      keys: [Keys.CMD, '\\'],
      action: toggleMenu,
    },
    pageBreak: {
      keys: [Keys.CMD, Keys.ENTER],
      action: injectPageBreak,
    },
  })

  function injectPageBreak() {
    observableEditor.invoke(insertPageBreak)
    focusEditor()
  }

  function injectDualDialogue() {
    mst.analytics.track('EDITOR_CREATE_DUAL_DIALOGUE')
    observableEditor.invoke(insertDualDialogue)
    focusEditor()
  }

  const handleMenuClose = () => {
    script.setElementMenuOpened(false)
    focusEditor()
  }

  // we display a custom icon when:
  // 1. the selection is within a single block
  // 2. a custom icon is actually present
  // 3. the menu in the toolbar isnt already open
  // 4. the user doesnt prefer to hide it
  const useCustomIcon = !selectionSpansBlocks && meta

  const showInlineButton =
    useCustomIcon && !opened && !restricted && !mst.user.prefs.hideBlockMenu

  const isPageless = !!mst.user.prefs.pageless

  return (
    <>
      {showInlineButton && (
        <InlineElementMenuButton
          key={`pageless-${isPageless}`} // to recalculate pos on the fly
          onClick={toggleMenu}
          isPageless={isPageless}
        >
          <ElementIcon node={meta.node} />
        </InlineElementMenuButton>
      )}
      <Menu
        shadow="md"
        closeOnEscape={true}
        opened={opened}
        width={275}
        onClose={handleMenuClose}
      >
        <Menu.Target>
          <Toolbar.Button
            aria-label={tooltip}
            customIcon={useCustomIcon && <ElementIcon node={meta.node} />}
            icon={!useCustomIcon ? 'fa-layer-group' : undefined}
            tooltip={tooltip}
            onClick={toggleMenu}
            label={targetTitle}
            mode={mode}
            dropdown
            fixedWidth={mode === 'normal' ? 120 : 40}
            disableTooltip={opened}
            disabled={restricted}
          />
        </Menu.Target>
        <Menu.Dropdown>
          <div style={{ maxHeight }}>
            {elementPickerKeys.map((item) => (
              <ElementMenuItem
                key={item.node.name}
                selected={item.node.name === curr}
                {...item}
              />
            ))}
            <Menu.Divider />
            <FancyMenuItem
              customIcon={<CustomIcon icon="icon-page-break" />}
              title="Insert page break"
              {...getItemProps('pageBreak')}
            />
            {script.isScreenplay && (
              <FancyMenuItem
                customIcon={<CustomIcon icon="icon-dual-dialogue" />}
                title="Insert dual dialogue"
                onClick={injectDualDialogue}
                disabled={!selectionTouchesEndOfBlock}
              />
            )}
            <Menu.Divider />
            <FancyMenuItem
              iconClass="fa fa-gear"
              title={
                <Group wrap="nowrap" gap={0}>
                  <Text lineClamp={3}>
                    Customize styles for&thinsp;
                    <Text span fw="bold">
                      {script.name}
                    </Text>
                  </Text>
                </Group>
              }
              onClick={() => showCustomizeStylesModal(script)}
            />
          </div>
        </Menu.Dropdown>
      </Menu>
    </>
  )
})

interface ElementMenuItemProps extends ElementKeyConfig {
  selected: boolean
}

function ElementMenuItem(props: ElementMenuItemProps) {
  'use memo'

  const mst = useMst()

  function handleClick() {
    mst.currentScript?.observableEditor?.invoke(props.command)
  }

  return (
    <FancyMenuItem
      key={props.node.name}
      customIcon={<ElementIcon node={props.node} />}
      title={props.displayName}
      selected={props.selected}
      shortcut={props.shortcut}
      onClick={handleClick}
    />
  )
}
